import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { registerServiceWorkerSEOFriendly, enhanceIOSPWA } from "./lib/pwa";
import "./lib/ios-safe-area";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { DialRoot } from "dialkit";
import "dialkit/styles.css";
import { MetaPixelPageView } from "./components/MetaPixelPageView";
import DemoContinuationCta from "./components/DemoContinuationCta";
import PreviewHomepage from "./pages/PreviewHomepage";
import EmptySlotRevenueLandingRefined from "./pages/EmptySlotRevenueLandingRefined";
import "./empty-slot-revenue-imessage.css";
import WabiInspiredLanding from "./pages/WabiInspiredLanding";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/dashboard/Dashboard";
import ServicesPage from "./pages/dashboard/Services";
import NotificationsPage from "./pages/dashboard/Notifications";
import BookingCalendarPage from "./pages/dashboard/BookingCalendar";
import BookingsPage from "./pages/dashboard/Bookings";
import { PlaceholderPage } from "./components/PlaceholderPage";
import { Suspense } from "react";
import ClientsPage from "./pages/dashboard/Clients";
import CustomersPage from "./pages/dashboard/Customers";
import CampaignsPage from "./pages/dashboard/Campaigns";
import BookingConfirmationPage from "./pages/booking/BookingConfirmationPage";
import BusinessSettingsPage from "./pages/dashboard/BusinessSettings";
import BlackoutDatesPage from "./pages/dashboard/BlackoutDates";
import BookingPageSettings from "./pages/dashboard/BookingPageSettings";
import HoursPage from "./pages/dashboard/Hours";
import SystemHealthPage from "./pages/dashboard/SystemHealth";
import FancySignIn from "./pages/fancy-signin/FancySignIn";
import PhoneOTPSignIn from "./pages/auth/PhoneOTPSignIn";
import LinkInBioDashboard from "./pages/linkinbio/Dashboard";
import LinkInBioPreviewPage from "./pages/linkinbio/Preview";
import MagicFlowPage from "./pages/MagicFlowPage";
import MagicDashboardPage from "./pages/MagicDashboardPage";
import MagicPathLinkInBioPage from "./pages/magicpath-linkinbio";
import PreviewFirstOnboarding from "./pages/PreviewFirstOnboarding";
import AuthCallback from "./pages/auth/Callback";
import EmailVerificationSuccess from "./pages/auth/EmailVerificationSuccess";
import BookingConfirmationEmailPreview from "./pages/dev/BookingConfirmationEmailPreview";
import TestBookingPage from "./pages/dev/TestBookingPage";
import TestPublicPage from "./pages/dev/TestPublicPage";
import { BookingCardThemePreviewWithDialKit } from "./components/booking-card/BookingCardThemePreviewWithDialKit";
import { TermsAndConditions } from "./pages/legal/TermsAndConditions";
import { CookiePolicy } from "./pages/legal/CookiePolicy";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";
import { AvatarTest } from "./components/test/AvatarTest";
import ManageLinkRedirect from "./pages/manage/ManageLinkRedirect";
import PaymentSuccessPage from "./pages/booking/PaymentSuccessPage";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import DraftListPage from "./pages/admin/DraftListPage";
import DraftNewPage from "./pages/admin/DraftNewPage";
import DraftEditorPage from "./pages/admin/DraftEditorPage";
import DraftPreviewPage from "./pages/DraftPreviewPage";
import OfferAcceptancePage from "./pages/offer/[token]";
import PublicBusinessPage from "./pages/PublicBusinessPage";
import UpgradeSuccess from "./pages/upgrade/Success";
import UpgradeCancel from "./pages/upgrade/Cancel";

const queryClient = new QueryClient();

function LSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/${slug}`} replace />;
}

function App() {
  React.useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      registerServiceWorkerSEOFriendly();
      enhanceIOSPWA();
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <MetaPixelPageView />
            <AuthProvider>
              <DemoContinuationCta />
              <Routes>
                <Route path="/" element={<PreviewHomepage />} />
                <Route path="/waitlist" element={<EmptySlotRevenueLandingRefined />} />
                <Route path="/invite" element={<WabiInspiredLanding />} />
                <Route path="/fancy-signin" element={<FancySignIn />} />
                <Route path="/phone-signin" element={<PhoneOTPSignIn />} />
                <Route path="/signup" element={<Navigate to="/magic-onboarding-v2?source=landing_cta" replace />} />
                <Route path="/magic-flow" element={<MagicFlowPage />} />
                <Route path="/magic-dashboard" element={<MagicDashboardPage />} />
                <Route path="/magicpath-linkinbio" element={<MagicPathLinkInBioPage />} />
                <Route path="/magic-onboarding" element={<Navigate to="/magic-onboarding-v2" replace />} />
                <Route path="/magic-onboarding-v2" element={<PreviewFirstOnboarding />} />
                <Route path="/test/avatars" element={<AvatarTest />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/email-verification-success" element={<EmailVerificationSuccess />} />
                {import.meta.env.DEV ? (
                  <>
                    <Route path="/dev/email/booking-confirmation" element={<BookingConfirmationEmailPreview />} />
                    <Route path="/dev/booking-card-preview" element={<BookingCardThemePreviewWithDialKit />} />
                    <Route path="/dev/test-booking" element={<TestBookingPage />} />
                    <Route path="/dev/test-public" element={<TestPublicPage />} />
                  </>
                ) : null}
                <Route path="/booking/confirmation/:id" element={<BookingConfirmationPage />} />
                <Route path="/offer/:token" element={<OfferAcceptancePage />} />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/payment-canceled" element={<PlaceholderPage title="Payment Canceled" description="Your payment has been canceled. Please try again." />} />
                <Route path="/upgrade/success" element={<UpgradeSuccess />} />
                <Route path="/upgrade/cancel" element={<UpgradeCancel />} />
                <Route path="/r/:slug" element={<ManageLinkRedirect />} />
                <Route path="/onboarding" element={<Navigate to="/magic-onboarding-v2" replace />} />
                <Route path="/legal/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/linkinbio/dashboard" element={<LinkInBioDashboard />} />
                  <Route path="/linkinbio/preview/:bookingUrl" element={<LinkInBioPreviewPage />} />
                </Route>
                <Route element={<ProtectedRoute />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard/bookings" element={<BookingsPage />} />
                  <Route path="/dashboard/calendar" element={<BookingCalendarPage />} />
                  <Route path="/dashboard/services" element={<ServicesPage />} />
                  <Route path="/dashboard/hours" element={<HoursPage />} />
                  <Route path="/dashboard/system-health" element={<SystemHealthPage />} />
                  <Route path="/dashboard/waitlist" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard/notifications" element={<NotificationsPage />} />
                  <Route path="/dashboard/clients" element={<ClientsPage />} />
                  <Route path="/dashboard/campaigns" element={<CampaignsPage />} />
                  <Route path="/dashboard/linkinbio" element={<LinkInBioDashboard />} />
                  <Route path="/dashboard/business-settings" element={<BusinessSettingsPage />} />
                  <Route path="/dashboard/booking-page" element={<BookingPageSettings />} />
                  <Route path="/dashboard/blackout-dates" element={<BlackoutDatesPage />} />
                  <Route path="/dashboard/booking-card" element={<Navigate to="/dashboard/booking-page" replace />} />
                  <Route path="/dashboard/booking-theme" element={<Navigate to="/dashboard/booking-page" replace />} />
                  <Route path="/dashboard/settings" element={<Navigate to="/dashboard/business-settings" replace />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route element={<ProtectedRoute requiredRole="admin" />}>
                    <Route path="/admin/drafts" element={<DraftListPage />} />
                    <Route path="/admin/drafts/new" element={<DraftNewPage />} />
                    <Route path="/admin/drafts/:draftId" element={<DraftEditorPage />} />
                  </Route>
                  <Route
                    path="/dashboard/customers"
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<div>Loading...</div>}>
                          <CustomersPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route path="/terms" element={<Navigate to="/legal/terms-and-conditions" replace />} />
                <Route path="/privacy" element={<Navigate to="/legal/privacy-policy" replace />} />
                <Route path="/l/:slug" element={<LSlugRedirect />} />
                <Route path="/preview/:previewToken" element={<DraftPreviewPage />} />
                <Route path="/:bookingUrl" element={<PublicBusinessPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
          <PWAInstallPrompt />
          {import.meta.env.DEV && <DialRoot position="top-right" />}
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
